import { Children, Fragment, cloneElement, isValidElement, useEffect, useId, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { AlertTriangle, Inbox, Loader2, Plus, Search, X } from 'lucide-react';
import { createPortal } from 'react-dom';
import { toast, type ToastMessage } from '@/lib/toast';
import type { Tone } from '@/lib/statusColors';
import { statusLabel, toneFor } from '@/lib/statusColors';

/** A status word in the colour its vocabulary gives it. `statusColors.ts` owns the mapping. */
export function StatusBadge({ label, tone = 'gray' }: { label: string; tone?: Tone }) {
  return <span className={`badge badge-${tone}`}>{label}</span>;
}


/**
 * Components shared by every live screen. They are deliberately plain: the interesting
 * decisions in this application are in the database, and a screen's job is to state what the
 * server said, including when it said no.
 */

export function LiveScreen({
  title,
  subtitle,
  actions,
  children,
}: { title: string; subtitle: string; actions?: ReactNode; children: ReactNode }) {
  // The page name is shown once, in the top bar (owner, 2026-09-21: "give the page name in the
  // header, delete the extra info"). `title` and `subtitle` stay as the section's accessible
  // name and description, so a screen reader still announces what the page is for.
  return (
    <section aria-description={subtitle} aria-label={title} className="page">
      {actions && <div className="header-tools">{actions}</div>}
      {children}
    </section>
  );
}

/**
 * One place to render loading, failure and emptiness, so no screen invents its own. A failed
 * query shows the server's own sentence: those are already written for a person, and replacing
 * them with "Something went wrong" would hide the only useful part.
 */
export function QueryState({
  query,
  empty,
  children,
}: {
  query: { isLoading: boolean; isError: boolean; error: unknown; data: unknown };
  empty?: string;
  children: ReactNode;
}) {
  if (query.isLoading) {
    return <p className="state-note"><Loader2 className="spin" size={16} /> Loading...</p>;
  }
  if (query.isError) {
    return (
      <p className="state-note state-error">
        <AlertTriangle size={16} /> {query.error instanceof Error ? query.error.message : 'That could not be loaded.'}
      </p>
    );
  }
  if (Array.isArray(query.data) && query.data.length === 0) {
    return <p className="state-note"><Inbox size={16} /> {empty ?? 'Nothing here yet.'}</p>;
  }
  return <>{children}</>;
}

export interface Column<T> {
  key: string;
  label: string;
  render?: (row: T) => ReactNode;
  align?: 'right';
}

export function RecordTable<T extends { id?: string | null }>({
  rows,
  columns,
  onRowClick,
  selectedId,
}: {
  rows: T[];
  columns: Array<Column<T>>;
  onRowClick?: (row: T) => void;
  selectedId?: string | null;
}) {
  return (
    <div className="grid-panel">
      <table className="record-table">
        <thead>
          <tr>{columns.map((column) => <th className={column.align} key={column.key}>{column.label}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr
              className={selectedId && row.id === selectedId ? 'selected' : undefined}
              key={row.id ?? index}
              onClick={onRowClick ? () => onRowClick(row) : undefined}
              style={onRowClick ? { cursor: 'pointer' } : undefined}
            >
              {columns.map((column) => (
                <td className={column.align} key={column.key}>
                  {column.render ? column.render(row) : String((row as Record<string, unknown>)[column.key] ?? '--')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function SearchBox({ value, onChange, placeholder }: { value: string; onChange: (value: string) => void; placeholder?: string }) {
  return (
    <label className="search-box">
      <Search size={16} />
      <input onChange={(event) => onChange(event.target.value)} placeholder={placeholder ?? 'Search'} value={value} />
      {value && <button onClick={() => onChange('')} title="Clear" type="button"><X size={14} /></button>}
    </label>
  );
}

export function Drawer({
  title,
  subtitle,
  open,
  onClose,
  children,
  footer,
  wide,
}: {
  title: string;
  subtitle?: string;
  open: boolean;
  onClose: () => void;
  children: ReactNode;
  footer?: ReactNode;
  wide?: boolean;
}) {
  const panel = useRef<HTMLElement>(null);
  // The latest onClose, read at the moment Escape is pressed. Parents pass a fresh arrow on every
  // render, and with onClose in the effect's dependencies every keystroke in a field re-ran the
  // effect - which pulls focus back to the panel - so a field took one character and lost focus.
  const closeRef = useRef(onClose);
  closeRef.current = onClose;

  /**
   * Escape closes, Tab stays inside, and focus starts in here rather than wherever it happened to
   * be. Without the last two a keyboard user opening this drawer was left on the page BEHIND it:
   * one Tab took them to the sidebar, and the fields could only be reached by tabbing through the
   * whole menu. `aria-modal` tells a screen reader the same thing this loop tells a keyboard.
   *
   * A half-typed form is easier to lose than to retype, so Escape and the buttons are the only
   * ways out - a click outside does not close it.
   */
  useEffect(() => {
    if (!open) return;
    const returnTo = document.activeElement as HTMLElement | null;
    const focusables = () => [...(panel.current?.querySelectorAll<HTMLElement>(
      'a[href], button:not(:disabled), input:not(:disabled), select:not(:disabled), textarea:not(:disabled), [tabindex]:not([tabindex="-1"])',
    ) ?? [])].filter((element) => element.offsetParent !== null);

    // The panel itself, not the first field: landing on a control skips the title, and a screen
    // reader then never reads what the drawer is for.
    panel.current?.focus();

    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { closeRef.current(); return; }
      if (event.key !== 'Tab') return;
      const list = focusables();
      if (list.length === 0) { event.preventDefault(); panel.current?.focus(); return; }
      const first = list[0]!;
      const last = list[list.length - 1]!;
      const active = document.activeElement;
      const inside = panel.current?.contains(active) && active !== panel.current;
      if (!inside) { event.preventDefault(); (event.shiftKey ? last : first).focus(); return; }
      if (event.shiftKey && active === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && active === last) { event.preventDefault(); first.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      // Back where they came from, so closing a drawer does not dump focus at the top of the page.
      if (returnTo && document.contains(returnTo)) returnTo.focus();
    };
  }, [open]);

  if (!open) return null;
  // Into <body>, not where it was declared. A drawer opened from the sticky mobile header (the
  // organisation switch, the reason-for-change prompt) was otherwise trapped in the header's
  // stacking context: under the phone's bottom nav, and under any page drawer already open - so the
  // reason prompt an operator must answer opened invisibly behind "Add a role" and the save hung.
  // As siblings in <body>, the one opened last is on top.
  return createPortal(
    <div className="drawer-scrim">
      {/* aria-modal keeps a screen reader's cursor inside the drawer; without it the page behind
          is still walkable and the reader has no way to tell the two apart. */}
      <aside aria-label={title} aria-modal="true" className={wide ? 'drawer drawer-wide' : 'drawer'}
             ref={panel} role="dialog" tabIndex={-1}>
        <header>
          <div>
            <h2>{title}</h2>
            {subtitle && <p>{subtitle}</p>}
          </div>
          <button onClick={onClose} title="Close" type="button"><X size={18} /></button>
        </header>
        <div className="drawer-body">{children}</div>
        {footer && <footer className="drawer-actions">{footer}</footer>}
      </aside>
    </div>,
    document.body,
  );
}

/**
 * Put the attributes on the control itself. Several controls render as a FRAGMENT - a text input
 * plus the datalist holding its suggestions - and a fragment silently swallows props, so the
 * first real element inside one is the target.
 */
function describeControl(node: ReactNode, props: Record<string, unknown>): ReactNode {
  if (!isValidElement(node)) return node;
  if (node.type !== Fragment) return cloneElement(node as ReactElement<Record<string, unknown>>, props);
  let applied = false;
  return Children.map((node.props as { children?: ReactNode }).children, (child) => {
    if (applied || !isValidElement(child)) return child;
    applied = true;
    return cloneElement(child as ReactElement<Record<string, unknown>>, props);
  });
}

export interface FieldProps {
  label: string;
  error?: string;
  hint?: string;
  required?: boolean;
  children: ReactNode;
}

/**
 * One labelled control.
 *
 * The hint and the error sit OUTSIDE the `<label>` on purpose. Inside it they became part of the
 * control's accessible name, so a screen reader read the field as "Site name * Site name is
 * required." - the name changing as the form was used, which is exactly what a name must not do.
 * They are the control's DESCRIPTION instead, wired by id, and an invalid control says so.
 *
 * `aria-describedby` reaches the control by cloning it, which works for a native input, select or
 * textarea and for any component that forwards the attribute (`Select` does). A control that
 * ignores it still renders and is still labelled; it simply carries no description.
 */
export function Field({ label, error, hint, required, children }: FieldProps) {
  const base = useId();
  const hintId = `${base}-hint`;
  const errorId = `${base}-error`;
  const describedBy = [error ? errorId : null, hint && !error ? hintId : null].filter(Boolean).join(' ') || undefined;
  const control = describeControl(children, {
    'aria-describedby': describedBy,
    'aria-invalid': error ? true : undefined,
  });
  return (
    <div className="live-field">
      <label>
        <span>{label}{required && <em aria-hidden> *</em>}</span>
        {control}
      </label>
      {hint && !error && <small id={hintId}>{hint}</small>}
      {error && <small className="field-error" id={errorId}>{error}</small>}
    </div>
  );
}

export function Select({
  value,
  onChange,
  options,
  placeholder,
  disabled,
  // Forwarded so `Field` can point the control at its error or hint. A component that swallows
  // these renders fine and simply carries no description.
  'aria-describedby': describedBy,
  'aria-invalid': invalid,
  'aria-label': ariaLabel,
}: {
  value: string;
  onChange: (value: string) => void;
  options: Array<{ id: string; label: string }>;
  placeholder?: string;
  disabled?: boolean;
  'aria-describedby'?: string;
  'aria-invalid'?: boolean;
  /** A select with no visible label of its own - a cell in a line table. */
  'aria-label'?: string;
}) {
  return (
    <select aria-describedby={describedBy} aria-invalid={invalid} aria-label={ariaLabel} disabled={disabled}
            onChange={(event) => onChange(event.target.value)} value={value}>
      <option value="">{placeholder ?? 'Select...'}</option>
      {options.map((option) => <option key={option.id} value={option.id}>{option.label}</option>)}
    </select>
  );
}

/** Status colour lives in `@/lib/statusColors` alone, so one word never means
 * two things across screens. */
export function StatusPill({ value }: { value?: string | null }) {
  if (!value) return <span className="muted">--</span>;
  return <StatusBadge label={statusLabel(value)} tone={toneFor(value)} />;
}

export function PrimaryButton({ children, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button className="primary" type="button" {...rest}>{children}</button>;
}

/**
 * The page's create action, as a floating action button (owner, 2026-09-21: "Add, Delete,
 * Update shall be FAB"). It sits bottom-right on every screen, above the phone's bottom nav,
 * so the primary action is always in the same place and within thumb reach. On a wide screen it
 * shows its label; on a phone it is a round "+" whose label is its accessible name.
 */
export function AddButton({ label, onClick, disabled, title }: { label: string; onClick: () => void; disabled?: boolean; title?: string }) {
  return (
    <button aria-label={label} className="fab" disabled={disabled} onClick={onClick}
            title={title ?? label} type="button">
      <Plus size={22} /> <span className="fab-label">{label}</span>
    </button>
  );
}

/** Toast host. Mounted once by the shell; every module publishes through `toast`. */
export function ToastHost() {
  const [messages, setMessages] = useState<ToastMessage[]>([]);
  // Errors go to a dialog that waits for OK (owner, 2026-09-21: "dialogue box for error in all
  // places upon save like point 1, 2, 3"). Errors arriving together are listed together.
  const [problems, setProblems] = useState<{ title: string; items: string[] } | null>(null);
  useEffect(() => toast.subscribe((message) => {
    if (message.tone === 'error') {
      const items = message.items ?? [message.text];
      setProblems((current) => (current && !message.title
        ? { title: current.title, items: [...current.items, ...items.filter((item) => !current.items.includes(item))] }
        : { title: message.title ?? 'That did not work', items }));
      return;
    }
    setMessages((current) => [...current, message]);
    setTimeout(() => setMessages((current) => current.filter((entry) => entry.id !== message.id)), 5000);
  }), []);
  useEffect(() => {
    if (!problems) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape' || event.key === 'Enter') setProblems(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [problems]);

  return (
    <>
      {problems && (
        <div className="error-dialog-scrim" onClick={() => setProblems(null)} role="presentation">
          <div aria-labelledby="error-dialog-title" aria-modal="true" className="error-dialog" onClick={(event) => event.stopPropagation()} role="alertdialog">
            <h2 id="error-dialog-title"><AlertTriangle size={18} /> {problems.title}</h2>
            {problems.items.length === 1
              ? <p>{problems.items[0]}</p>
              : <ol>{problems.items.map((item) => <li key={item}>{item}</li>)}</ol>}
            <div className="error-dialog-actions">
              <button autoFocus className="primary" onClick={() => setProblems(null)} type="button">OK</button>
            </div>
          </div>
        </div>
      )}
      {messages.length > 0 && <ToastList messages={messages} onClose={(id) => setMessages((current) => current.filter((entry) => entry.id !== id))} />}
    </>
  );
}

function ToastList({ messages, onClose }: { messages: ToastMessage[]; onClose: (id: number) => void }) {
  return (
    <div className="toast-host" role="status" aria-live="polite">
      {messages.map((message) => (
        <div className={`toast toast-${message.tone}`} key={message.id}>
          {message.text}
          <button onClick={() => onClose(message.id)} type="button">
            <X size={13} />
          </button>
        </div>
      ))}
    </div>
  );
}

/**
 * ONE date format platform-wide, the same as Hub: `dd-MMM-yyyy, EEE`
 * ("31-Aug-2026, Mon"), with a timestamp adding `h:mm AM/PM`.
 *
 * These four functions are the only producers. Never format a date with
 * toLocaleDateString in a component: `en-IN` renders "02 Sept 2026" - four-letter
 * month, spaces - which is how every generated document in Hub drifted while its
 * own documentation promised dd-MMM-yyyy. `yyyy-MM-dd` stays correct for input
 * values and query parameters; that is `toIsoDate` below.
 */
export function formatDate(value?: string | null): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const day = String(date.getDate()).padStart(2, '0');
  const month = date.toLocaleString('en-US', { month: 'short' });
  const weekday = date.toLocaleString('en-US', { weekday: 'short' });
  return `${day}-${month}-${date.getFullYear()}, ${weekday}`;
}

export function formatTime(value?: string | null): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  const hours = date.getHours() % 12 || 12;
  const suffix = date.getHours() < 12 ? 'AM' : 'PM';
  return `${hours}:${String(date.getMinutes()).padStart(2, '0')} ${suffix}`;
}

export function formatDateTime(value?: string | null): string {
  if (!value) return '--';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return `${formatDate(value)}, ${formatTime(value)}`;
}

/** The value a <input type="date"> and a query parameter want. Never a display format. */
export function toIsoDate(value?: string | Date | null): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  // Local parts, not toISOString(): that shifts to UTC and can move the date a day.
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
}

export function todayIso(): string {
  return toIsoDate(new Date());
}

export function useDebounced<T>(value: T, delay = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(timer);
  }, [value, delay]);
  return debounced;
}

/** Numbers read against each other in a column need the same shape. */
export function formatNumber(value?: number | string | null, digits = 0): string {
  if (value === null || value === undefined || value === '') return '--';
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(numeric)) return String(value);
  return numeric.toLocaleString('en-IN', { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/**
 * Money, the Hub way: rupee sign, Indian grouping, ALWAYS 2 decimals -
 * "Rs 12,34,567.89". Never hand-roll `₹${n.toLocaleString()}`: with no locale
 * that gives Western grouping, and ragged decimals make a right-aligned column
 * unreadable and stop a page footing.
 */
export function formatCurrency(value?: number | string | null): string {
  if (value === null || value === undefined || value === '') return '--';
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(numeric)) return String(value);
  return `₹${numeric.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

/**
 * Money on a SUMMARY TILE only - whole rupees, no decimals, because a tile is a
 * scale-at-a-glance figure and ".00" on every card is noise. Everything
 * transactional (grids, detail views, documents) stays on formatCurrency.
 */
export function formatCurrencyWhole(value?: number | string | null): string {
  if (value === null || value === undefined || value === '') return '--';
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(numeric)) return String(value);
  return `₹${numeric.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
}

export function useFormErrors() {
  const [errors, setErrors] = useState<Record<string, string>>({});
  return useMemo(() => ({
    errors,
    clear: () => setErrors({}),
    set: setErrors,
    /** Returns true when the form is clean, so callers read `if (!check(...)) return;` */
    check: (candidate: Record<string, string | undefined>) => {
      const next = Object.fromEntries(Object.entries(candidate).filter(([, value]) => value)) as Record<string, string>;
      setErrors(next);
      const problems = Object.values(next);
      // Every problem at once, numbered, in a dialog (owner, 2026-09-21) - as well as beside each
      // field - so nothing is missed on a long form or a field on another tab.
      if (problems.length > 0) {
        toast.errors(problems.length === 1 ? 'Please fix this before saving' : `Please fix these ${problems.length} things before saving`, problems);
      }
      return problems.length === 0;
    },
  }), [errors]);
}
